import type {ChangeEvent} from "react";
import {
    AddCircleOutlineOutlined,
    RemoveCircleOutlineOutlined,
    Search,
} from "@mui/icons-material";
import {
    Box,
    IconButton,
    InputAdornment,
    Tab,
    Tabs,
    TextField,
    useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import type {IncludeExcludeFilterCategory} from "../reducers/includeExcludeFilterReducer";
import {
    INCLUDE_EXCLUDE_BORDER,
    INCLUDE_EXCLUDE_HEIGHT_OF_HEADER,
} from "../IncludeExcludeFilter";

type TreeViewFilterSelectionHeaderProps = {
    selectedCategory?: IncludeExcludeFilterCategory;
    activeTab: number;
    setActiveTab: (tabIndex: number) => void;
    onModeSwitch?: (filterType: "include" | "exclude") => void;
    searchTerm: string;
    onSearchChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onClearSearch: () => void;
};

export function TreeViewFilterSelectionHeader({
    selectedCategory,
    activeTab,
    setActiveTab,
    onModeSwitch,
    searchTerm,
    onSearchChange,
    onClearSearch,
}: TreeViewFilterSelectionHeaderProps) {
    const theme = useTheme();
    const isIncludeOnly = !!selectedCategory?.includeOnly;

    return (
        <>
            <Box
                display="flex"
                alignItems="center"
                sx={{
                    borderBottom: INCLUDE_EXCLUDE_BORDER,
                    height: INCLUDE_EXCLUDE_HEIGHT_OF_HEADER,
                }}
            >
                {selectedCategory?.type === "checkbox" ? (
                    <TextField
                        fullWidth
                        placeholder={`Search ${selectedCategory.label || ""}...`}
                        variant="standard"
                        size="small"
                        value={searchTerm}
                        onChange={onSearchChange}
                        slotProps={{
                            input: {
                                startAdornment: <Search color="action" sx={{mr: 1}}/>,
                                endAdornment: searchTerm ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={onClearSearch}
                                            aria-label="Clear search"
                                        >
                                            <ClearIcon fontSize="small"/>
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                                disableUnderline: true,
                            },
                        }}
                        sx={{m: 1}}
                    />
                ) : (
                    <Box sx={{m: 1, width: "100%", height: 40}}/>
                )}
            </Box>

            <Tabs
                value={activeTab}
                onChange={(_, newValue) => {
                    setActiveTab(newValue);
                    onModeSwitch?.(newValue === 0 ? "include" : "exclude");
                }}
                variant="fullWidth"
                sx={{
                    minHeight: "32px",
                    borderBottom: INCLUDE_EXCLUDE_BORDER,
                    "& .MuiTab-root": {
                        minHeight: "32px",
                        padding: 0,
                        fontSize: "10px",
                    },
                    display: isIncludeOnly ? "none" : undefined,
                }}
            >
                <Tab
                    label={
                        <Box>
                            <AddCircleOutlineOutlined
                                sx={{
                                    color: theme.palette.success.main,
                                    fontSize: "12px",
                                    mr: 0.5,
                                }}
                            />{" "}
                            INCLUDE
                        </Box>
                    }
                    sx={{padding: "4px"}}
                />
                <Tab
                    label={
                        <Box>
                            <RemoveCircleOutlineOutlined
                                sx={{
                                    color: theme.palette.error.light,
                                    fontSize: "12px",
                                    mr: 0.5,
                                }}
                            />{" "}
                            EXCLUDE
                        </Box>
                    }
                    sx={{padding: "4px"}}
                />
            </Tabs>
        </>
    );
}
